import React, { useState, useEffect } from 'react';
import { Button, Tag, message, Select, Space } from 'antd';
import { PlusOutlined, EditOutlined, CopyOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useDrawer, useGlobalForm, useTable, useConfirmModal } from '@/hooks/index';
import { GlobalTable, ConfirmModal } from '@/index';
import GlobalForm from '@/components/GlobalForm/GlobalForm';
import { deviceModelsAPI } from '@/services/api/device-models';
import { DeviceModel as APIDeviceModel, CreateDeviceModelRequest, UpdateDeviceModelRequest, PaginationParams } from '@/types/api';
import type { FormField } from '@/types/form';
import type { TableColumn, TableAction } from '@/types/table';
import { requiredRule } from '@/utils/formHelper';

// 使用API类型，添加key字段用于表格
type DeviceModel = APIDeviceModel & { key: string };

/**
 * 设备型号表单配置（动态生成）
 */
const createDeviceModelFields = (
  chipVendors: string[],
  operatingSystems: string[],
  deviceModelStatuses: Array<{ value: string; label: string }>
): FormField[] => [
  // 基本信息分组标题
  {
    name: 'group_basic',
    label: '',
    type: 'custom',
    customRender: () => (
      <div style={{ 
        fontSize: '16px', 
        fontWeight: 600, 
        color: 'var(--text-primary)',
        marginBottom: '16px',
        marginTop: '8px',
        paddingBottom: '8px',
        borderBottom: '2px solid var(--color-primary)'
      }}>
        基本信息
      </div>
    ),
    colSpan: 24,
  },
  {
    name: 'name',
    label: '机型名称',
    type: 'input',
    placeholder: '例如：X9 Pro',
    rules: [requiredRule('请输入机型名称')],
    colSpan: 24,
  },
  {
    name: 'chip_vendor',
    label: '芯片厂商',
    type: 'select',
    placeholder: '请选择芯片厂商',
    rules: [requiredRule('请选择芯片厂商')],
    options: chipVendors.map(vendor => ({ label: vendor, value: vendor })),
    colSpan: 12,
  },
  {
    name: 'chip_model',
    label: '芯片型号',
    type: 'input',
    placeholder: '例如：Amlogic S905X4',
    rules: [requiredRule('请输入芯片型号')],
    colSpan: 12,
  },
  
  // 系统配置分组标题
  {
    name: 'group_system',
    label: '',
    type: 'custom',
    customRender: () => (
      <div style={{ 
        fontSize: '16px', 
        fontWeight: 600, 
        color: 'var(--text-primary)',
        marginBottom: '16px',
        marginTop: '24px',
        paddingBottom: '8px',
        borderBottom: '2px solid var(--color-primary)'
      }}>
        系统配置
      </div>
    ),
    colSpan: 24,
  },
  {
    name: 'os',
    label: '操作系统',
    type: 'select',
    placeholder: '请选择操作系统',
    rules: [requiredRule('请选择操作系统')],
    options: operatingSystems.map(os => ({ label: os, value: os })),
    colSpan: 12,
  },
  {
    name: 'os_version',
    label: '系统版本',
    type: 'input',
    placeholder: '例如：11.0',
    rules: [requiredRule('请输入系统版本')],
    colSpan: 12,
  },

  // 其他信息分组标题
  {
    name: 'group_other',
    label: '',
    type: 'custom',
    customRender: () => (
      <div style={{ 
        fontSize: '16px', 
        fontWeight: 600, 
        color: 'var(--text-primary)',
        marginBottom: '16px',
        marginTop: '24px',
        paddingBottom: '8px',
        borderBottom: '2px solid var(--color-primary)'
      }}>
        其他信息
      </div>
    ),
    colSpan: 24,
  },
  {
    name: 'status',
    label: '状态',
    type: 'select',
    placeholder: '请选择状态',
    rules: [requiredRule('请选择状态')],
    options: deviceModelStatuses,
    colSpan: 12,
  },
];

/**
 * 表单组件（用于Drawer中）
 */
interface DeviceModelFormProps {
  mode: 'add' | 'edit';
  initialValues?: any;
  onSuccess?: () => void;
  fields: FormField[];
}

const DeviceModelForm: React.FC<DeviceModelFormProps> = ({ mode, initialValues, onSuccess, fields }) => {
  const { closeDrawer } = useDrawer();
  const { formRef } = useGlobalForm();
  const [loading, setLoading] = useState(false);

  const handleFinish = async (values: any) => {
    try {
      setLoading(true);
      
      // 提交数据
      const submitData = {
        ...values,
      };
      
      console.log('提交的数据:', submitData);
      
      if (mode === 'add') {
        await deviceModelsAPI.createDeviceModel(submitData as CreateDeviceModelRequest);
        message.success('添加成功！');
      } else {
        await deviceModelsAPI.updateDeviceModel(initialValues?.id, submitData as UpdateDeviceModelRequest);
        message.success('更新成功！');
      }
      
      closeDrawer();
      onSuccess?.();
    } catch (error) {
      console.error('操作失败:', error);
      message.error('操作失败，请重试！');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <GlobalForm
        ref={formRef}
        fields={fields}
        initialValues={initialValues}
        columns={1}
        showSubmit={false}
        onFinish={handleFinish}
      />
      
      <div style={{ 
        marginTop: 24, 
        paddingTop: 16, 
        borderTop: '1px solid var(--border-primary)',
        display: 'flex',
        gap: 8,
      }}>
        <Button 
          type="primary" 
          loading={loading}
          onClick={() => formRef.current?.submit()}
        >
          {mode === 'add' ? '添加' : '保存'}
        </Button>
        <Button onClick={closeDrawer}>取消</Button>
      </div>
    </div>
  );
};

const DeviceModelManagement: React.FC = () => {
  const { openDrawer } = useDrawer();
  const { tableRef, loading, dataSource, setDataSource, pagination, updatePagination } = useTable<DeviceModel>();
  const { config, showConfirmModal, handleConfirm, handleCancel } = useConfirmModal();
  
  // 筛选状态
  const [chipVendorFilter, setChipVendorFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  
  // 选项数据状态
  const [chipVendors, setChipVendors] = useState<string[]>([]);
  const [operatingSystems, setOperatingSystems] = useState<string[]>([]);
  const [deviceModelStatuses, setDeviceModelStatuses] = useState<Array<{ value: string; label: string }>>([]);

  // 获取机型列表
  const fetchDeviceModels = async (params?: PaginationParams & { status?: string; chip_vendor?: string }) => {
    try {
      const response = await deviceModelsAPI.getDeviceModels({
        page: pagination.current,
        size: pagination.pageSize,
        search: searchKeyword,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        chip_vendor: chipVendorFilter !== 'all' ? chipVendorFilter : undefined,
        ...params,
      });
      
      // 添加key字段用于表格
      if (!response.list || response.list.length === 0) {
        setDataSource([]);
        updatePagination(pagination.current, pagination.pageSize, 0);
        return;
      }
      const modelsWithKey = response.list.map(model => ({
        ...model,
        key: model.id.toString(),
      }));
      
      setDataSource(modelsWithKey);
      updatePagination(pagination.current, pagination.pageSize, response.total);
    } catch (error) {
      console.error('获取机型列表失败:', error);
      message.error('获取机型列表失败');
    }
  };

  // 获取选项数据
  const fetchOptions = async () => {
    try {
      const [chipVendorsData, operatingSystemsData, deviceModelStatusesData] = await Promise.all([
        deviceModelsAPI.getChipVendors(),
        deviceModelsAPI.getOperatingSystems(),
        deviceModelsAPI.getDeviceModelStatuses(),
      ]);
      
      setChipVendors(chipVendorsData);
      setOperatingSystems(operatingSystemsData);
      setDeviceModelStatuses(deviceModelStatusesData);
    } catch (error) {
      console.error('获取选项数据失败:', error);
    }
  };

  // 初始化数据
  useEffect(() => {
    fetchDeviceModels();
    fetchOptions();
  }, []);

  // 统计信息
  // const totalModels = mockData.length;
  // const producingModels = mockData.filter(m => m.status === 'producing').length;

  /**
   * 复制到剪贴板
   */
  const copyToClipboard = async (text: string, label: string = '内容') => {
    try {
      await navigator.clipboard.writeText(text);
      message.success(`${label}已复制到剪贴板`);
    } catch (error) {
      console.error('复制失败:', error);
      message.error('复制失败，请重试');
    }
  };

  /**
   * 格式化 secret_key 显示（显示头尾，中间用 **** 代替）
   */
  const formatSecretKey = (secretKey: string | undefined): string => {
    if (!secretKey) return '-';
    if (secretKey.length <= 8) return '****';
    // 显示前4位和后4位，中间用 **** 代替
    const prefix = secretKey.substring(0, 4);
    const suffix = secretKey.substring(secretKey.length - 4);
    return `${prefix}****${suffix}`;
  };

  /**
   * 列配置
   */
  const columns: TableColumn<DeviceModel>[] = [
    {
      title: '机型名称',
      dataIndex: 'name',
      key: 'name',
      width: 120,
    },
    {
      title: '芯片',
      key: 'chip',
      width: 150,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.chip_model}</div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{record.chip_vendor}</div>
        </div>
      ),
    },
    {
      title: '操作系统',
      key: 'os',
      width: 130,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.os}</div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{record.os_version}</div>
        </div>
      ),
    },
    {
      title: 'Model Key',
      dataIndex: 'model_key',
      key: 'model_key',
      width: 200,
      render: (value: string) => {
        if (!value) return '-';
        return (
          <Space>
            <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{value}</span>
            <CopyOutlined
              style={{ 
                color: 'var(--color-primary)', 
                cursor: 'pointer',
                fontSize: '14px'
              }}
              onClick={(e) => {
                e.stopPropagation();
                copyToClipboard(value, 'Model Key');
              }}
            />
          </Space>
        );
      },
    },
    {
      title: 'Secret Key',
      dataIndex: 'secret_key',
      key: 'secret_key',
      width: 200,
      render: (value: string) => {
        if (!value) return '-';
        const displayValue = formatSecretKey(value);
        return (
          <Space>
            <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{displayValue}</span>
            <CopyOutlined
              style={{ 
                color: 'var(--color-primary)', 
                cursor: 'pointer',
                fontSize: '14px'
              }}
              onClick={(e) => {
                e.stopPropagation();
                copyToClipboard(value, 'Secret Key');
              }}
            />
          </Space>
        );
      },
    },
    {
      title: '创建日期',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: (value: string) => value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '-',
    },
    {
      title: '设备数量',
      dataIndex: 'device_count',
      key: 'device_count',
      width: 110,
      render: (count: number) => <span style={{ color: 'var(--color-primary)', fontWeight: 600 }}>{count} 台</span>,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => {
        const statusConfig = {
          in_production: { color: 'green', text: '生产中' },
          discontinued: { color: 'red', text: '已停产' },
        };
        const config = statusConfig[status as keyof typeof statusConfig] || { color: 'default', text: status };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
  ];

  /**
   * 处理删除
   */
  const handleDelete = (record: DeviceModel) => {
    showConfirmModal('delete', {
      deleteConfig: {
        itemName: record.name,
        warningText: '删除后该机型将不再显示，关联的设备将无法获取版本更新。',
        showWarning: true,
      },
      onConfirm: async () => {
        try {
          await deviceModelsAPI.deleteDeviceModel(record.id);
          message.success('删除成功');
          fetchDeviceModels();
        } catch (error) {
          console.error('删除失败:', error);
          message.error('删除失败，请重试');
        }
      },
    });
  };

  /**
   * 操作列配置
   */
  const actions: TableAction<DeviceModel>[] = [
    {
      label: <EditOutlined />,
      key: 'edit',
      type: 'text',
      onClick: (record) => handleEdit(record),
    },
    {
      label: <DeleteOutlined />,
      key: 'delete',
      type: 'text',
      danger: true,
      onClick: (record) => handleDelete(record),
    },
  ];

  /**
   * 处理添加
   */
  const handleAdd = () => {
    const fields = createDeviceModelFields(chipVendors, operatingSystems, deviceModelStatuses);
    openDrawer({
      title: '添加机型',
      content: (
        <DeviceModelForm 
          mode="add"
          fields={fields}
          onSuccess={() => {
            //message.success('添加成功');
            fetchDeviceModels();
          }}
        />
      ),
      width: 800,
    });
  };

  /**
   * 处理编辑
   */
  const handleEdit = (record: DeviceModel) => {
    // 设置表单初始值
    const formValues = {
      ...record,
    };
    
    const fields = createDeviceModelFields(chipVendors, operatingSystems, deviceModelStatuses);
    openDrawer({
      title: '编辑机型',
      content: (
        <DeviceModelForm 
          mode="edit"
          initialValues={formValues}
          fields={fields}
          onSuccess={() => {
            fetchDeviceModels();
          }}
        />
      ),
      width: 800,
    });
  };

  /**
   * 处理搜索
   */
  const handleSearch = (value: string) => {
    setSearchKeyword(value);
    updatePagination(1, pagination.pageSize, pagination.total);
    fetchDeviceModels({
      page: 1,
      size: pagination.pageSize,
      search: value,
      status: statusFilter !== 'all' ? statusFilter : undefined,
      chip_vendor: chipVendorFilter !== 'all' ? chipVendorFilter : undefined,
    });
  };



  return (
    <>
      <div className="space-y-6">
        <GlobalTable
          ref={tableRef}
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          header={{
            title: '机型列表',
            subtitle: `当前支持的机型列表`,
            extra: (
              <Button 
                type="primary" 
                icon={<PlusOutlined />} 
                size="large"
                onClick={handleAdd}
                className="bg-accent-blue hover:bg-blue-600"
              >
                添加机型
              </Button>
            ),
          }}
          toolbar={{
            showSearch: true,
            searchPlaceholder: '搜索机型名称、芯片...',
            onSearch: handleSearch,
            actions: [
              <Select
                key="chip-vendor-filter"
                placeholder="全部芯片厂商"
                style={{ width: 140 }}
                size="large"
                value={chipVendorFilter}
                onChange={(value) => {
                  setChipVendorFilter(value);
                  updatePagination(1, pagination.pageSize, pagination.total);
                  fetchDeviceModels({
                    page: 1,
                    size: pagination.pageSize,
                    search: searchKeyword,
                    status: statusFilter !== 'all' ? statusFilter : undefined,
                    chip_vendor: value !== 'all' ? value : undefined,
                  });
                }}
                options={[
                  { label: '全部芯片厂商', value: 'all' },
                  ...chipVendors.map(vendor => ({ label: vendor, value: vendor })),
                ]}
              />,
              <Select
                key="status-filter"
                placeholder="全部状态"
                style={{ width: 140 }}
                size="large"
                value={statusFilter}
                onChange={(value) => {
                  setStatusFilter(value);
                  updatePagination(1, pagination.pageSize, pagination.total);
                  fetchDeviceModels({
                    page: 1,
                    size: pagination.pageSize,
                    search: searchKeyword,
                    status: value !== 'all' ? value : undefined,
                    chip_vendor: chipVendorFilter !== 'all' ? chipVendorFilter : undefined,
                  });
                }}
                options={[
                  { label: '全部状态', value: 'all' },
                  ...deviceModelStatuses,
                ]}
              />,
            ],
          }}
          actions={actions}
          actionsWidth={100}
          pagination={{
            ...pagination,
            showTotal: true,
            onChange: (page, pageSize) => {
              updatePagination(page, pageSize, pagination.total);
              fetchDeviceModels({
                page,
                size: pageSize,
                search: searchKeyword,
                status: statusFilter !== 'all' ? statusFilter : undefined,
                chip_vendor: chipVendorFilter !== 'all' ? chipVendorFilter : undefined,
              });
            },
          }}
          scroll={{ x: 1400 }}
          
        />
      </div>
      
      {/* 全局确认弹框 */}
      <ConfirmModal 
        config={{
          ...config,
          onConfirm: handleConfirm,
          onCancel: handleCancel,
        }}
      />
    </>
  );
};

export default DeviceModelManagement;
